/** Car trips into the charging zone, from /api/traffic/od-flows: what the flow map
 *  draws in each mode and income class, and the figures its caption gives. On a pair,
 *  n trips are eligible to switch mode and r of them did; c holds both per class. */

/** Baseline, or one of what the Scenario mode shows. `legend` titles the arrows,
 *  `hexLegend` the hexagons. 'share' draws the shifted trips as arrows and colours the
 *  hexagons by the share of their trips that shifted. */
export const FLOW_MODES = [
  { id: 'baseline', label: 'Baseline', legend: 'Car trips into the zone, no charge', hexLegend: 'Car trips into the zone, by where they start' },
  { id: 'scenario', label: 'Trips that remain', legend: 'Car trips into the zone that remain', hexLegend: 'Car trips into the zone that remain, by where they start' },
  { id: 'shifted', label: 'Shifted (trips)', legend: 'Shifted trips: the ones the charge moved to other modes', hexLegend: 'Shifted trips, by where they start' },
  { id: 'share', label: 'Shifted (% of trips)', legend: 'Arrows: number of shifted trips', hexLegend: 'Share of each hexagon’s trips into the zone that switched from driving' },
];

/** The Scenario mode's Show list; the first is its default. */
export const SCENARIO_SHOWS = FLOW_MODES.filter((m) => m.id !== 'baseline');

/** Of n trips, r shifted: those drawn in `mode`. */
export const modeTrips = (mode, n, r) => (mode === 'baseline' ? n : mode === 'scenario' ? n - r : r);

/** Destination ids are <taz>#in: the part of a TAZ cut by the cordon that lies inside it. */
export const tazOf = (id) => String(id).replace(/#in$/, '');

const pairOf = (f, classId) => (classId ? (f.c && f.c[classId]) || [0, 0] : [f.n, f.r || 0]);

/** flowmap.gl's data: every location, and each pair's trips in `mode`. */
export function flowView(doc, mode, classId = null) {
  const flows = [];
  for (const f of doc.flows) {
    const [n, r] = pairOf(f, classId);
    const count = modeTrips(mode, n, r);
    if (count > 0) flows.push({ origin: f.o, dest: f.d, count });
  }
  return { locations: doc.locations, flows };
}

/** Eligible trips and the ones shifted to other modes (null for the baseline): the
 *  window's totals, or a class's, or those of the pairs that start or end at one of
 *  `locationIds`. byClass: { <class>: [eligible, shifted] } for the same trips, null
 *  where the file has no classes. */
export function flowTotals(doc, classId = null, locationIds = null) {
  const hasShift = doc.scenario !== 'baseline';
  if (!locationIds) {
    const [eligible, shifted] = classId
      ? (doc.totals.by_class && doc.totals.by_class[classId]) || [0, 0]
      : [doc.totals.eligible, doc.totals.removed];
    return { eligible, shifted: hasShift ? shifted : null, byClass: doc.totals.by_class || null };
  }
  const ids = new Set(locationIds);
  const classes = doc.classes || [];
  const byClass = classes.length ? Object.fromEntries(classes.map((k) => [k, [0, 0]])) : null;
  let eligible = 0;
  let shifted = 0;
  for (const f of doc.flows) {
    if (!ids.has(f.o) && !ids.has(f.d)) continue;
    const [n, r] = pairOf(f, classId);
    eligible += n;
    shifted += r;
    if (byClass && f.c) {
      for (const k of classes) {
        const pair = f.c[k];
        if (pair) {
          byClass[k][0] += pair[0];
          byClass[k][1] += pair[1];
        }
      }
    }
  }
  return { eligible, shifted: hasShift ? shifted : null, byClass };
}
